import { Loader, Menu } from "dash-styled"
import React, { FC, ReactNode, useMemo, useState } from "react"
import { useCollaborators } from "../../../../../../../Hooks/useCollaborators"
import {
    AIModelAssignmentEnum,
    hasActiveAssignment
} from "../../../../../../../Models/API/AIModel"
import { defaultParams } from "../../../../../../../Models/API/CollaboratorModel"
import { hasAppPermission } from "../../../../../../../Models/Client/ProjectRole"
import {
    getValue,
    hasValue
} from "../../../../../../../Models/Client/FetchState"
import { AppDetailBase } from "../../AppDetailBase/AppDetailBase"
import { AIModelStatus } from "./Common/AIModelStatus"
import { ReactComponent as IconLock } from "icons/lib/lock.svg"
import { useNeqlContextProvider } from "../../../../../../../Hooks/useNeqlContextProvider"
import { metaAI } from "neql"
import { Map } from "immutable"
import { ReactComponent as IconSpinner } from "icons/lib/spinner.svg"
import { DetailSection } from "./Common/DetailSection"
import { ActivitySection } from "./Common/ActivitySection"
import { useDispatch } from "react-redux"
import {
    failMessage,
    successMessage
} from "../../../../../../../Actions/Client/MessagesActions"
import { routePaths } from "../../../../../../../Routes"
import { replace } from "connected-react-router"
import { useAssumedRoles } from "../../../../../../../Hooks/useAssumedRoles"

export interface AIModelDetailProps {
    orgUsername: string
    appID: string
    modelID: string
}

type Action = "remove" | "toggleActiveness"

/**
 * No longer used, kept for future reuse
 */
export const AIModelDetail: FC<AIModelDetailProps> = ({
    orgUsername,
    appID,
    modelID
}) => {
    const dispatch = useDispatch()
    const client = useNeqlContextProvider("metaAIApp", appID)
    const { data: modelFetch, refetch: refetchModel } =
        metaAI.useGetAiModelForAppQuery({
            client,
            variables: { appId: appID, modelId: modelID, days: 7 }
        })
    const [updateAIModelAssignmentActiveness] =
        metaAI.useUpdateAiModelAssignmentActivenessMutation({ client })
    const [updateAIModelAllAssignmentsActiveness] =
        metaAI.useUpdateAiModelAllAssignmentsActivenessMutation({ client })
    const [removeAIModel, { loading: isRemovingAIModel }] =
        metaAI.useDeleteMetaAiAppMutation({ client })
    const collaboratorsFetch = useCollaborators(appID, defaultParams)
    const rolesFetch = useAssumedRoles(appID)
    const [processingAssignments, setProcessingAssignments] = useState<
        Map<AIModelAssignmentEnum, true>
    >(Map<AIModelAssignmentEnum, true>())
    const roles = useMemo(() => getValue(rolesFetch) ?? [], [rolesFetch])
    const handleChangeActiveness = useMemo(() => {
        if (!hasAppPermission(roles, "ai:update")) {
            return undefined
        }

        return (
            assigned: AIModelAssignmentEnum,
            active: boolean,
            useLatestAi: boolean
        ) => {
            setProcessingAssignments(a => a.set(assigned, true))
            updateAIModelAssignmentActiveness({
                variables: {
                    appId: appID,
                    modelId: modelID,
                    assigned,
                    active,
                    useLatestAi
                }
            })
                .then(() => {
                    const message = active
                        ? "Model is activated"
                        : "Model is paused"

                    dispatch(successMessage(message))
                    refetchModel()
                })
                .catch((e: unknown) =>
                    dispatch(failMessage("Could not update model", e))
                )
                .finally(() =>
                    setProcessingAssignments(a => a.remove(assigned))
                )
        }
    }, [
        appID,
        dispatch,
        modelID,
        roles,
        refetchModel,
        updateAIModelAssignmentActiveness
    ])

    const model = modelFetch?.meta_ai_model_by_pk ?? undefined
    const actionMenuItems = useMemo(() => {
        const ret: { value: Action; label: ReactNode }[] = []

        if (model === undefined) {
            return ret
        }

        if (hasAppPermission(roles, "ai:update")) {
            ret.push({
                value: "toggleActiveness",
                label: (
                    <div className="flex items-center">
                        {hasActiveAssignment(model)
                            ? "Pause all usages"
                            : "Resume all usages"}
                        {!processingAssignments.isEmpty() && (
                            <IconSpinner className="ml-1 w-3 h-3 text-smoke-810" />
                        )}
                    </div>
                )
            })
        }

        if (hasAppPermission(roles, "ai:remove")) {
            ret.push({
                value: "remove",
                label: (
                    <div className="flex items-center">
                        Remove model from project
                        {isRemovingAIModel && (
                            <IconSpinner className="ml-1 w-3 h-3 text-smoke-810" />
                        )}
                    </div>
                )
            })
        }

        return ret
    }, [isRemovingAIModel, model, roles, processingAssignments])

    const handleActionMenuItem = useMemo(() => {
        if (actionMenuItems.length === 0 || model === undefined) {
            return undefined
        }

        return (selected: Action) => {
            if (selected === "remove") {
                removeAIModel({
                    variables: {
                        appId: appID,
                        modelId: model.id
                    }
                })
                    .then(() => {
                        dispatch(successMessage("AI model removed"))
                        dispatch(
                            replace(routePaths.appModelList(orgUsername, appID))
                        )
                    })
                    .catch((e: unknown) =>
                        dispatch(failMessage("Could not remove model", e))
                    )
                return
            }

            setProcessingAssignments(
                Map(model.apps.map(assignment => [assignment.assigned, true]))
            )

            const active = !hasActiveAssignment(model)

            return updateAIModelAllAssignmentsActiveness({
                variables: {
                    appId: appID,
                    modelId: modelID,
                    active
                }
            })
                .then(() => {
                    const message = active
                        ? "Model is activated"
                        : "Model is paused"

                    dispatch(successMessage(message))
                    refetchModel()
                })
                .catch((e: unknown) =>
                    dispatch(failMessage("Could not update model", e))
                )
                .finally(() =>
                    setProcessingAssignments(Map<AIModelAssignmentEnum, true>())
                )
        }
    }, [
        actionMenuItems.length,
        appID,
        dispatch,
        model,
        modelID,
        orgUsername,
        refetchModel,
        removeAIModel,
        updateAIModelAllAssignmentsActiveness
    ])

    if (model === undefined || !hasValue(collaboratorsFetch)) {
        return (
            <AppDetailBase
                orgUsername={orgUsername}
                id={appID}
                title={null}
                breadcrumbs={[]}
                fixedContentWidth={false}
            >
                <Loader />
            </AppDetailBase>
        )
    }

    return (
        <AppDetailBase
            orgUsername={orgUsername}
            id={appID}
            title={
                <div className="flex items-center gap-4 lg:gap-8">
                    <div className="flex-grow">
                        {model.name} (version {model.version})
                    </div>
                    {model.visibility === "PRIVATE" && (
                        <div className="text-rhino-900 text-sm font-normal">
                            <IconLock className="text-smoke-910 inline-block w-4 h-4 mr-1" />
                            Private
                        </div>
                    )}
                    <AIModelStatus
                        className="text-sm font-normal"
                        model={model}
                    />
                    {handleActionMenuItem !== undefined && (
                        <Menu
                            testId="model-actions"
                            className="font-normal"
                            title="Actions"
                            align="right"
                            nonInteractive={false}
                            items={actionMenuItems}
                            onChoose={handleActionMenuItem}
                        />
                    )}
                </div>
            }
            breadcrumbs={["AI Models"]}
            fixedContentWidth={false}
        >
            <div className="flex flex-col lg:flex-row lg:items-start gap-8 py-8">
                <DetailSection
                    className="lg:w-1/2"
                    model={model}
                    collaborators={collaboratorsFetch.value.items}
                    processingAssignments={processingAssignments}
                    onChangeActiveness={handleChangeActiveness}
                />
                <ActivitySection className="lg:w-1/2" model={model} />
            </div>
        </AppDetailBase>
    )
}
